import moment from 'moment';
import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { Store } from '../../Store';
import CustomTypeahead from '../../components/CustomTypeahead';
import { Button, Checkbox, FormField, Section, Select, controlClass } from '../../components/EntityList';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import accountService from '../../services/account.service';
import contractService from '../../services/contract.service';
import creditService from '../../services/credit.service';
import creditConceptService from '../../services/creditConcept.service';
import {
	FRONTEND_LEGACY_CREDIT_META,
	FALLBACK_CREDIT_CONCEPT_NAMES,
	behaviorNeedsCapital,
	behaviorNeedsDues,
	behaviorNeedsInterest,
	buildConceptData,
	buildConceptMeta,
	deriveConceptDefaults,
	getMeta,
	normalizeConceptName,
	validateByMeta,
} from '../../utils/conceptMeta';
import { currencies, dueDays, getError, monthOptions, periodicities } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

// Maps the Spanish labels collectMissingFields()/validateByMeta() already
// produce onto the field-error keys used for inline messages below each
// input, so the missing-field logic stays in one place.
const FIELD_ERROR_KEYS = {
	'Fecha': 'date',
	'Concepto': 'concept',
	'Otro Concepto': 'otroConcepto',
	'Monto': 'amount',
	'Moneda': 'currency',
	'Cuenta': 'owner',
	'Datos generación cuota': 'firstDueExpiration',
	'Contrato': 'contract',
	'Cuotas': 'dues',
	'Capital': 'capital',
	'Interés': 'interest',
	'Periodicidad': 'periodicity',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_ACCOUNT_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ACCOUNT_SUCCESS':
		return { ...state, loadingFetch: false };
	case 'FETCH_ACCOUNTS_SUCCESS':
		return { ...state, accounts: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	case 'CLEAR_ERROR':
		return { ...state, error: '' };
	default:
		return state;
	}
};

/**
 * Full-page credit create/edit form (see screens/Debts/DebtEditor.jsx for the
 * pattern this mirrors). Renders only the form body — the page shell
 * (EntityList/PageHead) lives in the page components that use this
 * (NewCredit.jsx, EditCredit.jsx, and the contextual create/edit modal on the
 * account "Pasividades" tab).
 *
 * Editing a credit's start date/schedule is handled separately by
 * CreditDateEditor.jsx (reached from the credit detail page), since changing
 * it here would desync already-generated dues — same split Debts already
 * uses (DebtEditor.jsx + DebtDateEditor.jsx).
 */
const CreditEditor = ({
	mode = 'create',
	creditId = null,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	preloadAccountId = null,
}) => {
	const { t } = useLabels();
	const { state } = useContext(Store);
	const isEditing = mode === 'edit';
	const location = useLocation();
	const [searchParams] = useSearchParams();
	// Injected via ?accountId= after a "crear nueva cuenta" round trip (see
	// NewAccount.jsx). Deliberately not cleared — linkToCreate below carries
	// location.search forward, so a second round trip keeps earlier selections.
	const injectedAccountId = searchParams.get('accountId');
	const consumedAccountId = useRef(null);
	// Pre-selects the concept (and therefore which fields render) when this
	// editor is reached with ?concept=<slug> in the URL.
	const injectedConcept = searchParams.get('concept');
	const consumedConcept = useRef(null);
	// Injected via ?contractId= after a "crear nuevo alquiler" round trip
	// (see NewContract.jsx) — mirrors the injectedAccountId handling above.
	const injectedContractId = searchParams.get('contractId');
	const consumedContractId = useRef(null);

	const [{ loading, saving, error, accounts }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		error: '',
		accounts: [],
	});
	const [fieldErrors, setFieldErrors] = useState({});

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	// Edit mode fetches the credit by id when the caller didn't already supply
	// initialData (mirrors DebtEditor.jsx's debtId-fetch effect).
	const [fetchedCredit, setFetchedCredit] = useState(null);
	const resolvedInitialData = initialData || fetchedCredit;

	const [capital, setCapital] = useState();
	const [amount, setAmount] = useState(0);
	const [interest, setInterest] = useState();
	const [selectedConcept, setSelectedConcept] = useState();
	const [concept, setConcept] = useState();
	const [concepts, setConcepts] = useState([]);
	const [conceptMap, setConceptMap] = useState({});
	const [conceptMetaMap, setConceptMetaMap] = useState({});
	const [dues, setDues] = useState(1);
	const [date, setDate] = useState();
	const [contract, setContract] = useState();
	const [periodicity, setPeriodicity] = useState('');
	const [owner, setOwner] = useState();
	const [disabledInput, setDisabledInput] = useState(true);
	const [hasPreviousCredits, setHasPreviousCredits] = useState(true);
	const [previousCredit, setPreviousCredit] = useState();
	const [previousCreditDate, setPreviousCreditDate] = useState();
	const [currency, setCurrency] = useState();
	const [endDate, setEndDate] = useState();
	const [disabledAmount, setDisabledAmount] = useState(false);
	const [createWithPreviousDues, setCreateWithPreviousDues] = useState(false);
	const [createWithPreviousPayments, setCreateWithPreviousPayments] = useState(false);
	const [dueDay, setDueDay] = useState('10');
	const [firstDueExpiration, setFirstDueExpiration] = useState();
	const [numMonths, setNumMonths] = useState(0);
	const [selectedMonthOption, setSelectedMonthOption] = useState('Mes siguiente');
	const [periodicityNumMonths, setPeriodicityNumMonths] = useState();
	const [isInvalid, setIsInvalid] = useState(false);
	const [hydrated, setHydrated] = useState(false);
	const [contracts, setContracts] = useState([]);

	// Metadata for the selected concept drives which blocks render and how the
	// form validates. Legacy slugs resolve through the frontend legacy map when
	// the concept doc carries no behavior. Credits never render the per-user
	// billing block (the Credit model has no billingType field).
	const meta = getMeta(selectedConcept, conceptMetaMap, FRONTEND_LEGACY_CREDIT_META);
	// Complex concepts render the loan inputs (capital/interest/dues/calc).
	const isComplexConcept = (key) =>
		behaviorNeedsCapital(getMeta(key, conceptMetaMap, FRONTEND_LEGACY_CREDIT_META).behavior);

	// accountService.list() runs before a just-created account exists, so keep
	// the selected owner in the options list (otherwise it can't be
	// re-selected from the dropdown after being cleared).
	const accountOptions = useMemo(
		() => (owner?._id && !accounts.some((acc) => acc._id === owner._id) ? [owner, ...accounts] : accounts),
		[accounts, owner],
	);

	// Same "keep the current selection in the options list" guard as
	// accountOptions above — contracts is refetched fresh, so a just-created
	// contract from the round trip below is already in it, but a contract
	// selected before a stale refetch resolves would otherwise disappear.
	const contractOptions = useMemo(() => {
		const selectedContract = contract?.[0];
		return selectedContract && !contracts.some((c) => c._id === selectedContract._id)
			? [selectedContract, ...contracts]
			: contracts;
	}, [contracts, contract]);

	const fetchCreditConcepts = async () => {
		try {
			const result = await creditConceptService.list({ active: true });
			const activeConcepts = result.data
				.sort((a, b) => (a.order || 0) - (b.order || 0));
			setConceptMetaMap(buildConceptMeta(activeConcepts));

			const map = {};
			const conceptValues = activeConcepts
				.filter(c => c.name)
				.map(c => {
					const normalized = normalizeConceptName(c.name);
					map[normalized] = c.name;
					return normalized;
				});

			setConcepts(conceptValues);
			setConceptMap(map);
		} catch (error) {
			console.error('Error fetching credit concepts:', error);
			toast.error(t('msg.conceptsLoadFailed'));
			setConceptMetaMap({});
			// Legacy slugs (only those with a display name; the unaccented
			// 'prestamos-interes' duplicate and contract-bound slugs are skipped).
			const fallbackConcepts = Object.keys(FRONTEND_LEGACY_CREDIT_META)
				.filter((key) => FALLBACK_CREDIT_CONCEPT_NAMES[key]);
			setConcepts(fallbackConcepts);
			setConceptMap({ ...FALLBACK_CREDIT_CONCEPT_NAMES });
		}
	};

	const fetchAccountData = async () => {
		try {
			dispatch({ type: preloadAccountId ? 'FETCH_ACCOUNT_REQUEST' : 'FETCH_REQUEST' });
			if (preloadAccountId) {
				const result = await accountService.getDetails(preloadAccountId);
				setOwner(result.data);
				setCurrency(result.data.currency);
				dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: result.data });
			} else {
				const result = await accountService.list();
				dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: result.data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.accountLoadFailed'));
		}
	};

	// Only the requiresContract periodic behavior (e.g. 'alquileres') needs
	// this — fetched unconditionally alongside accounts/concepts anyway,
	// mirroring DebtEditor.jsx's equivalent contract fetch.
	const fetchContractsData = async () => {
		try {
			const result = preloadAccountId
				? await contractService.getContractsForAccount(preloadAccountId)
				: await contractService.list();
			setContracts(result.data?.data || result.data || []);
		} catch (error) {
			console.error('Error fetching contracts:', error);
			toast.error(t('msg.contractLoadFailed'));
		}
	};

	const fetchCreditById = async () => {
		if (initialData || !creditId) {
			return;
		}
		try {
			const result = await creditService.getDetails(creditId);
			setFetchedCredit(result.data);
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			console.error(error);
			toast.error('No se pudo obtener la pasividad');
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				await Promise.all([fetchCreditById(), fetchAccountData(), fetchCreditConcepts(), fetchContractsData()]);
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				console.error(ex);
				dispatch({ type: 'FETCH_FAIL', payload: getError(ex) });
				toast.error(getError(ex));
			}
		};
		fetchData();
	}, [creditId, preloadAccountId]);

	// Hydrate the form from the credit being edited, once accounts and the
	// concept list are available (the owner typeahead needs its options loaded
	// before a selection can be shown).
	useEffect(() => {
		if (!isEditing || !resolvedInitialData || hydrated) return;
		if (!preloadAccountId && accounts.length === 0) return;
		if (concepts.length === 0) return;
		const data = resolvedInitialData;

		const normalized = data.concept ? normalizeConceptName(data.concept) : undefined;
		setSelectedConcept(normalized);
		setConcept(data.concept);
		if (data.amount !== undefined && data.amount !== null) setAmount(data.amount);
		if (data.capital !== undefined && data.capital !== null && data.capital !== '') setCapital(data.capital);
		if (data.interest !== undefined && data.interest !== null && data.interest !== '') setInterest(data.interest);
		if (data.dues) setDues(data.dues);
		if (data.date) setDate(moment(data.date).toDate());
		if (data.periodicity) setPeriodicity(data.periodicity);
		if (data.periodicityNumMonths) setPeriodicityNumMonths(data.periodicityNumMonths);
		if (data.currency) setCurrency(data.currency);
		if (data.endDate) setEndDate(moment(data.endDate).toDate());
		if (data.dueDay) setDueDay(String(data.dueDay));
		if (data.selectedMonthOption) setSelectedMonthOption(data.selectedMonthOption);
		if (data.numMonths) setNumMonths(data.numMonths);
		if (data.firstDueExpiration) setFirstDueExpiration(moment.utc(data.firstDueExpiration).format('YYYY-MM-DD'));
		setHasPreviousCredits(!!data.hasPreviousCredits);
		if (data.previousCredit) setPreviousCredit(data.previousCredit);
		if (data.previousCreditDate) setPreviousCreditDate(moment(data.previousCreditDate).toDate());
		// Skip when an account was just injected via ?accountId= (see the effect
		// below) — otherwise whichever of the two writers to owner resolves last
		// wins, nondeterministically.
		if (data.owner && !injectedAccountId) {
			const ownerObj = typeof data.owner === 'object'
				? data.owner
				: accounts.find((a) => a._id === data.owner);
			if (ownerObj) {
				setOwner(ownerObj);
				if (!data.currency && ownerObj.currency) setCurrency(ownerObj.currency);
			}
		}
		if (data.contract) {
			setContract(typeof data.contract === 'object' ? [data.contract] : [{ _id: data.contract }]);
		}
		setDisabledInput(false);
		setHydrated(true);
	}, [isEditing, resolvedInitialData, hydrated, accounts, concepts, conceptMetaMap, injectedAccountId]);

	// Auto-select an account created via the "crear nueva cuenta" link
	// (CustomTypeahead's linkToCreate -> NewAccount.jsx), which returns here
	// with ?accountId=<id>. The ref only de-dupes the fetch — StrictMode
	// double-invokes this in development. Do NOT reintroduce an isCurrent
	// cleanup flag here: combined with clearing the dependency it aborts the
	// invocation that does the work.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		consumedAccountId.current = injectedAccountId;

		(async () => {
			try {
				const { data } = await accountService.getDetails(injectedAccountId);
				setOwner(data);
				setIsInvalid(false);
				if (data.currency) setCurrency(data.currency);
				clearFieldError('owner');
			} catch (error) {
				console.error(error);
				toast.error(t('msg.accountLoadFailed'));
			}
		})();
	}, [injectedAccountId]);

	// Auto-select a contract created via the "crear nuevo alquiler" link
	// (CustomTypeahead's linkToCreate -> NewContract.jsx), which returns here
	// with ?contractId=<id>. Mirrors the injectedAccountId effect above.
	useEffect(() => {
		if (!injectedContractId || consumedContractId.current === injectedContractId) return;
		consumedContractId.current = injectedContractId;

		(async () => {
			try {
				const { data } = await contractService.getDetails(injectedContractId);
				setContract([data]);
				if (data.periodEnd) setEndDate(moment(data.periodEnd).toDate());
				clearFieldError('contract');
			} catch (error) {
				console.error(error);
				toast.error(t('msg.contractLoadFailed'));
			}
		})();
	}, [injectedContractId]);

	// Pre-selects the concept when reached with ?concept=<slug> (create mode
	// only — edit mode hydrates concept from the credit being edited).
	useEffect(() => {
		if (isEditing || !injectedConcept || consumedConcept.current === injectedConcept) return;
		if (concepts.length === 0) return;
		if (!concepts.includes(injectedConcept)) return;
		consumedConcept.current = injectedConcept;
		handleSelectedConcept({ target: { value: injectedConcept } });
	}, [isEditing, injectedConcept, concepts]);

	const generateDate = () => {
		if (!date) return;
		const currentDate = moment.utc(date, 'YYYY-MM-DD');
		let futureDate;

		if (selectedMonthOption === 'Mes actual') {
			futureDate = currentDate.clone();
		} else if (selectedMonthOption === 'Mes siguiente') {
			futureDate = currentDate.clone().add(1, 'month');
		} else if (selectedMonthOption === 'En tres meses' || selectedMonthOption === 'En seis meses') {
			const monthsToAdd = selectedMonthOption === 'En tres meses' ? 3 : 6;
			futureDate = currentDate.clone().add(monthsToAdd, 'month');
		} else if (selectedMonthOption === 'Otro') {
			futureDate = currentDate.clone().add(numMonths, 'month');
		}

		if (futureDate) {
			futureDate.date(Math.min(parseInt(dueDay), futureDate.daysInMonth()));
			const expiration = moment.utc(futureDate).format('YYYY-MM-DD');
			setFirstDueExpiration(expiration);
			return expiration;
		}
	};

	const dateHandler = () => {
		if (date && moment.utc(date).isBefore(moment.utc())) {
			setCreateWithPreviousDues(true);
		} else {
			setCreateWithPreviousDues(false);
			setCreateWithPreviousPayments(false);
		}
	};

	useEffect(() => {
		if (isEditing) return;
		generateDate();
		dateHandler();
	}, [date, selectedMonthOption, dueDay, numMonths, isEditing]);

	const collectMissingFields = () => {
		const emptyFields = [];

		if (!isEditing && !date) {
			emptyFields.push('Fecha');
		}
		if (!selectedConcept && !concept) {
			emptyFields.push('Concepto');
		}
		if (selectedConcept === 'otro' && !concept) {
			emptyFields.push('Otro Concepto');
		}
		if (amount === undefined || amount === null || amount === '') {
			emptyFields.push('Monto');
		}
		if (!currency) {
			emptyFields.push('Moneda');
		}
		emptyFields.push(
			...validateByMeta(meta, { periodicity, capital, interest, dues, amount, contract })
		);
		if (!owner) {
			emptyFields.push('Cuenta');
		}
		if (!isEditing && !firstDueExpiration) {
			emptyFields.push('Datos generación cuota');
		}
		return emptyFields;
	};

	const getFieldErrors = () => {
		const emptyFields = collectMissingFields();
		const errors = {};
		emptyFields.forEach((label) => {
			const key = FIELD_ERROR_KEYS[label];
			if (key) errors[key] = REQUIRED_FIELD_MESSAGE;
		});
		return { errors, emptyFields };
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const { errors, emptyFields } = getFieldErrors();
		if (emptyFields.length > 0) {
			setFieldErrors(errors);
			dispatch({ type: 'SAVE_FAIL', payload: `${t('msg.completeFields')}: ${emptyFields.join(', ')}.` });
			return;
		}
		setFieldErrors({});
		if (!isEditing && moment.utc(firstDueExpiration).isBefore(moment.utc(date))) {
			toast.error(t('msg.expirationBeforeDate'));
			return;
		}

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			if (!owner) {
				toast.error(t('msg.selectAccount'));
				dispatch({ type: 'SAVE_FAIL', payload: '' });
				return;
			}
			const requestData = {
				amount,
				concept,
				owner: owner[0] || owner,
				currency,
				periodicity,
			};
			// The start date is read-only while editing (CreditDateEditor owns it),
			// but PUT /credits/:id lists `date` as required AND assigns
			// `credit.date = req.body.date` unconditionally — omitting it both
			// failed validation with "Fecha" while the field showed a value, and
			// would have wiped the stored date. Send the hydrated value back
			// unchanged.
			if (date) {
				requestData.date = moment.utc(date);
			}
			if (!isEditing) {
				requestData.guaranteedAmount = 0;
				requestData.dueDay = dueDay;
				requestData.selectedMonthOption = selectedMonthOption;
				requestData.firstDueExpiration = moment.utc(firstDueExpiration);
				requestData.createWithPreviousDues = createWithPreviousDues;
				requestData.createWithPreviousPayments = createWithPreviousPayments;
				requestData.createdBy = state.userInfo._id;
			}
			// Concept-specific fields (contract / capital / interest / dues) are
			// applied per behavior, preserving each legacy concept's payload.
			buildConceptData(
				meta,
				{ contract, endDate, interest, dues, capital, periodicity },
				requestData,
				{ entityType: 'credit' }
			);
			if (hasPreviousCredits) {
				requestData.hasPreviousCredits = hasPreviousCredits;
				requestData.previousCredit = previousCredit;
				requestData.previousCreditDate = previousCreditDate;
			} else {
				requestData.hasPreviousCredits = false;
				requestData.previousCredit = '';
				requestData.previousCreditDate = '';
			}
			if (!isEditing && selectedMonthOption === 'Otro') {
				requestData.numMonths = numMonths;
			}
			if (periodicity === 'otra') {
				requestData.periodicityNumMonths = periodicityNumMonths;
			}

			let savedCreditId = creditId || resolvedInitialData?._id;
			if (isEditing) {
				const result = await creditService.edit(savedCreditId, requestData);
				savedCreditId = result.data?.credit?._id || savedCreditId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.creditUpdated'));
			} else {
				const result = await creditService.create(
					requestData,
					createWithPreviousDues,
					createWithPreviousPayments,
				);
				savedCreditId = result.data?.credit?._id || savedCreditId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.creditCreated'));
			}

			if (onSaved) {
				onSaved(savedCreditId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			toast.error(message);
		}
	};

	// `capital`/`interest`/`dues` can arrive as the number 0 after edit-mode
	// hydration (a legitimate 0% interest loan) — a plain `!value` truthiness
	// check would treat that as missing, so check for blank/NaN instead.
	const isBlankNumber = (value) => value === undefined || value === null || value === '' || isNaN(Number(value));

	function getValorDeCuotaFijaMensualAmortizable() {
		if (isBlankNumber(capital) || isBlankNumber(interest) || isBlankNumber(dues)) {
			toast.error(t('msg.enterCapitalInterestDues'));
			return;
		} else {
			if (Number(interest) === 0) {
				const valorCuota = capital / dues;
				setAmount(valorCuota);
			} else {
				const tasa = interest / 100;
				const valorCuota =
					capital * ((tasa * Math.pow(1 + tasa, dues)) / (Math.pow(1 + tasa, dues) - 1));
				setAmount(Math.ceil(valorCuota));
			}
		}
	}

	function getValorDeCuotaFijaMensualInteres() {
		if (isBlankNumber(capital) || isBlankNumber(interest)) {
			toast.error(t('msg.enterCapitalInterest'));
			return;
		}
		const tasa = interest / 100;
		const valorCuota = capital * tasa;
		setAmount(Math.ceil(valorCuota));
	}

	function getValorEntregaCuenta() {
		if (isBlankNumber(capital) || isBlankNumber(dues)) {
			toast.error(t('msg.enterCapitalDues'));
			return;
		}
		const valorCuota = capital / dues;
		setAmount(Math.ceil(valorCuota));
	}

	function AmountCalc() {
		if (meta.behavior === 'account-delivery') {
			getValorEntregaCuenta();
			setDisabledAmount(false);
		} else if (meta.behavior === 'amortizable' || meta.behavior === 'installment-sale') {
			getValorDeCuotaFijaMensualAmortizable();
			setDisabledAmount(false);
		} else if (meta.behavior === 'interest-only') {
			getValorDeCuotaFijaMensualInteres();
		}
	}

	const handleAccountChange = (selected) => {
		if (!selected || selected.length === 0) {
			setIsInvalid(false);
			setOwner(null);
			setCurrency('');
			setDisabledAmount(false);
		} else {
			const selectedAccount = selected[0];
			const isValid = selectedAccount ? accountOptions.some(acc => acc._id === selectedAccount._id) : false;
			setIsInvalid(!isValid);
			setOwner(selectedAccount);
			setCurrency(selectedAccount.currency);
			setDisabledAmount(true);
			clearFieldError('owner');
		}
	};

	const handleContractChange = (selected) => {
		if (!selected || selected.length === 0) {
			setContract(null);
			setEndDate(undefined);
		} else {
			const selectedContract = selected[0];
			setContract(selected);
			if (selectedContract.periodEnd) setEndDate(moment(selectedContract.periodEnd).toDate());
			clearFieldError('contract');
		}
	};

	const handleSelectedConcept = (e) => {
		const value = e.target.value;
		setSelectedConcept(value);
		setDisabledInput(false);
		const nextMeta = getMeta(value, conceptMetaMap, FRONTEND_LEGACY_CREDIT_META);

		// The required-field set changes wholesale with the concept, so any
		// summary/inline errors from the previous concept must not survive
		// the switch (they'd otherwise describe fields that no longer apply —
		// e.g. "Capital" lingering after switching to a contract-based
		// concept that doesn't use it).
		setFieldErrors({});
		dispatch({ type: 'CLEAR_ERROR' });

		if (value !== 'otro') {
			setConcept(value);
		}

		// Drop values the new concept's behavior no longer requires —
		// otherwise a field hidden by the new behavior can still submit a
		// stale value.
		if (!behaviorNeedsCapital(nextMeta.behavior)) {
			setCapital(undefined);
			setInterest(undefined);
		}
		if (!nextMeta.requiresContract) {
			setContract(undefined);
		}

		// Periodicity/dues are derived fresh from the new concept rather than
		// left over from the previous one — this is the actual fix for
		// "required fields from the previous concept": leaving periodicity at
		// its old value (or forcing it to 'efectivo' when the new behavior
		// needs a real choice) is what produced a stuck/incorrect required set.
		const { periodicity: nextPeriodicity, dues: nextDues } = deriveConceptDefaults(nextMeta);
		setPeriodicity(nextPeriodicity);
		setDues(nextDues);
	};

	const handlePeriodicityChange = (e) => {
		const value = e.target.value;
		setPeriodicity(value);
		clearFieldError('periodicity');
		if (value === 'efectivo') {
			setDues(1);
			clearFieldError('dues');
		}
	};

	if (loading) return <DetailPageLoader entityLabel='editor' />;

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');

	return (
		<Form id='credit-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos de la pasividad.' : 'Completá los datos para crear la pasividad.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button
						variant='primary'
						icon={isEditing ? <FaRegSave /> : <MdAdd />}
						type='submit'
						disabled={saving || loading}
					>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section
				title={t('title.dueGenerationData')}
				description={isEditing ? 'La fecha de inicio y el cronograma de generación no se editan aquí — usá "Editar fecha de inicio" en el detalle de la pasividad.' : undefined}
			>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-4'>
					{isEditing ? (
						<>
							<FormField id='credit-date' label={t('form.creditStartDateLong')}>
								<Form.Control
									id='credit-date'
									className={controlClass}
									value={date ? moment.utc(date).format('DD-MM-YYYY') : t('msg.noData')}
									disabled
								/>
							</FormField>
							<FormField id='credit-due-day' label={t('form.firstDueDay')}>
								<Form.Control id='credit-due-day' className={controlClass} value={dueDay || t('msg.noData')} disabled />
							</FormField>
							<FormField id='credit-generate-from' label={t('form.generateFrom')}>
								<Form.Control
									id='credit-generate-from'
									className={controlClass}
									value={selectedMonthOption || t('msg.noData')}
									disabled
								/>
							</FormField>
						</>
					) : (
						<>
							<FormField id='credit-date' label={t('form.creditStartDate')} required>
								<DatePicker
									id='credit-date'
									selected={date}
									onChange={(d) => {
										setDate(d);
										clearFieldError('date');
									}}
									dateFormat='dd-MM-yyyy'
									className={`form-control ${controlClass}`}
									placeholderText='dd-mm-yyyy'
								/>
								<FieldError message={fieldErrors.date} />
							</FormField>
							<FormField id='credit-due-day' label={t('form.firstDueDay')} required>
								<Select
									id='credit-due-day'
									options={dueDays}
									value={dueDay}
									onChange={(e) => setDueDay(e.target.value)}
								/>
							</FormField>
							<FormField id='credit-generate-from' label={t('form.generateFrom')} required>
								<Select
									id='credit-generate-from'
									options={monthOptions}
									value={selectedMonthOption}
									onChange={(e) => setSelectedMonthOption(e.target.value)}
								/>
								<FieldError message={fieldErrors.firstDueExpiration} />
							</FormField>
							{selectedMonthOption === 'Otro' && (
								<FormField id='credit-num-months' label={t('form.numMonths')} required>
									<Form.Control
										id='credit-num-months'
										className={controlClass}
										type='number'
										min='1'
										max='240'
										value={numMonths}
										onChange={(e) => setNumMonths(parseInt(e.target.value, 10))}
										onInput={(e) => {
											const inputValue = parseInt(e.target.value, 10);
											const maxValue = 240;
											if (maxValue !== null && inputValue > maxValue) {
												e.target.value = maxValue;
											}
										}}
									/>
								</FormField>
							)}
						</>
					)}
				</div>
				{!isEditing && date && (
					<p className='tw:mt-3 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>
						{`${t('msg.firstExpiration')}: ${moment.utc(firstDueExpiration).format('DD-MM-YYYY')}`}
					</p>
				)}
			</Section>

			{createWithPreviousDues && !isEditing && (
				<Section title={t('title.debtWithPreviousDues')} description={t('msg.previousDuesCreditWarning')}>
					<Checkbox
						label={t('form.markPreviousDuesPaid')}
						checked={createWithPreviousPayments}
						onChange={() => setCreateWithPreviousPayments(!createWithPreviousPayments)}
					/>
				</Section>
			)}

			<Section title={t('title.accountingInfo')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='credit-concept' label={t('entity.concept')} required>
						<Select
							id='credit-concept'
							options={concepts.map((c) => ({ value: c, label: conceptMap[c] || c }))}
							value={selectedConcept}
							onChange={handleSelectedConcept}
							placeholder={t('entity.concept')}
						/>
						<FieldError message={fieldErrors.concept || fieldErrors.otroConcepto} />
					</FormField>
					<FormField id='credit-owner' label={t('entity.account.creditor')} required>
						<CustomTypeahead
							id='accounts'
							labelKey={(option) => `${option.name}`}
							onChange={handleAccountChange}
							options={accountOptions}
							disabled={!!preloadAccountId}
							selected={owner ? [owner] : []}
							placeholder={t('form.placeholder.searchAccount')}
							linkToCreate={`/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}${selectedConcept ? `&concept=${encodeURIComponent(selectedConcept)}` : ''}`}
							newOption='nueva cuenta'
							variant='entity'
						/>
						{isInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidAccount')}</div>}
						<FieldError message={fieldErrors.owner} />
					</FormField>
					<FormField id='credit-currency' label={t('field.currency')} required>
						<Select
							id='credit-currency'
							options={currencies}
							value={currency}
							onChange={(e) => {
								setCurrency(e.target.value);
								clearFieldError('currency');
							}}
							placeholder={t('field.currency')}
						/>
						<FieldError message={fieldErrors.currency} />
					</FormField>
					{selectedConcept && isComplexConcept(selectedConcept) ? (
						<>
							{meta.behavior !== 'account-delivery' && (
								<FormField id='credit-periodicity' label={t('field.periodicity')} required>
									<Select
										id='credit-periodicity'
										options={periodicities}
										value={periodicity}
										onChange={handlePeriodicityChange}
										placeholder={t('field.periodicity')}
									/>
									<FieldError message={fieldErrors.periodicity} />
								</FormField>
							)}
							{periodicity === 'otra' && (
								<FormField id='credit-periodicity-months' label={t('form.periodicityMonths')}>
									<FormatedNumberInput
										className={controlClass}
										value={periodicityNumMonths ?? ''}
										onChange={(e) => setPeriodicityNumMonths(e.target.value)}
										min={1}
										noDecimal={true}
									/>
								</FormField>
							)}
							{periodicity !== 'efectivo' && (
								<>
									<FormField id='credit-capital' label={t('field.capital')} required>
										<FormatedNumberInput
											className={controlClass}
											value={capital ?? ''}
											onChange={(e) => {
												setCapital(e.target.value);
												clearFieldError('capital');
											}}
											min={0}
										/>
										<FieldError message={fieldErrors.capital} />
									</FormField>
									{behaviorNeedsInterest(meta.behavior) && (
										<FormField id='credit-interest' label={t('form.interestPercent')} required>
											<FormatedNumberInput
												className={controlClass}
												value={interest ?? ''}
												onChange={(e) => {
													setInterest(e.target.value);
													clearFieldError('interest');
												}}
												min={0}
											/>
											<FieldError message={fieldErrors.interest} />
										</FormField>
									)}
									{behaviorNeedsDues(meta.behavior) && (
										<FormField id='credit-dues' label={t('entity.due.plural')} required>
											<FormatedNumberInput
												className={controlClass}
												value={dues ?? ''}
												onChange={(e) => {
													setDues(e.target.value);
													clearFieldError('dues');
												}}
												min={1}
												noDecimal={true}
											/>
											<FieldError message={fieldErrors.dues} />
										</FormField>
									)}
								</>
							)}
							<FormField id='credit-amount-calc' label={`${t('field.amount')} ${behaviorNeedsCapital(meta.behavior) ? 'cuota' : ''}`} required>
								<div className='tw:flex tw:gap-2'>
									<FormatedNumberInput
										className={`${controlClass} tw:flex-1`}
										value={amount}
										disabled={disabledAmount}
										onChange={(e) => {
											setAmount(e.target.value);
											clearFieldError('amount');
										}}
										min={0}
									/>
									<Button size='sm' onClick={AmountCalc} disabled={disabledInput}>
										{t('action.calculate')}
									</Button>
								</div>
								<FieldError message={fieldErrors.amount} />
							</FormField>
						</>
					) : selectedConcept && meta.requiresContract ? (
						<>
							<FormField id='credit-contract' label={t('field.contract')} required>
								<CustomTypeahead
									id='credit-contract'
									labelKey={(option) => `${option.customId}`}
									onChange={handleContractChange}
									options={contractOptions}
									selected={contract || []}
									placeholder={t('form.placeholder.searchContractGeneric')}
									linkToCreate={`/AdminScreen/newContract?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
									newOption='nuevo contrato'
									variant='entity'
								/>
								<FieldError message={fieldErrors.contract} />
							</FormField>
							<FormField id='credit-amount-contract' label={t('field.amount')} required>
								<FormatedNumberInput
									className={controlClass}
									value={amount}
									onChange={(e) => {
										setAmount(e.target.value);
										clearFieldError('amount');
									}}
									min={0}
								/>
								<FieldError message={fieldErrors.amount} />
							</FormField>
						</>
					) : selectedConcept ? (
						<>
							<FormField id='credit-periodicity-simple' label={t('field.periodicity')} required>
								<Select
									id='credit-periodicity-simple'
									options={periodicities}
									value={periodicity}
									onChange={handlePeriodicityChange}
									placeholder={t('field.periodicity')}
								/>
								<FieldError message={fieldErrors.periodicity} />
							</FormField>
							<FormField id='credit-amount-simple' label={t('field.amount')} required>
								<FormatedNumberInput
									className={controlClass}
									value={amount}
									onChange={(e) => {
										setAmount(e.target.value);
										clearFieldError('amount');
									}}
									min={0}
								/>
								<FieldError message={fieldErrors.amount} />
							</FormField>
						</>
					) : null}
				</div>
			</Section>

			<Section title={t('title.offSystemRecord')} description={t('msg.offSystemTooltip')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-3 tw:items-end'>
					<div className='tw:flex tw:items-center tw:h-[34px]'>
						<Checkbox
							label={t('form.hasPreviousCredit')}
							checked={hasPreviousCredits}
							onChange={(e) => setHasPreviousCredits(e.target.checked)}
						/>
					</div>
					<FormField id='credit-previous-credit' label={t('field.previousCredit')}>
						<FormatedNumberInput
							className={controlClass}
							onChange={(e) => setPreviousCredit(e.target.value)}
							disabled={!hasPreviousCredits}
							min={0}
						/>
					</FormField>
					<FormField id='credit-previous-credit-date' label={t('form.previousCreditDate')}>
						<DatePicker
							selected={previousCreditDate ? previousCreditDate : null}
							onChange={(d) => setPreviousCreditDate(d)}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
							disabled={!hasPreviousCredits}
						/>
					</FormField>
				</div>
			</Section>
		</Form>
	);
};

CreditEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	creditId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	preloadAccountId: PropTypes.string,
};

export default CreditEditor;
